import {useEffect} from 'react';
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Loader2} from 'lucide-react';
import {useToast} from '@/components/ui/use-toast.ts';
import {Button} from '@/components/ui/button.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Textarea} from "@/components/ui/textarea.tsx";
import {Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle} from "@/components/ui/sheet.tsx";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form.tsx';
import {UpdateMentorMetaDto, Mentor} from "@/types/Mentor.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {updateMentorWithMetadata} from "@/services/mentorService.ts";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {regionOptions} from "@/utils/profile-info.ts";
import {useTranslation} from "react-i18next";
import {activeLocale, LocalizedField} from "@/utils/translation-helper.ts";
import {translateText} from "@/services/translateService.ts";

interface MentorFormProps {
    isOpen: boolean;
    onOpenChange: (value: boolean) => void;
    mentor: Mentor | null;
}

const MentorForm = ({isOpen, onOpenChange, mentor}: MentorFormProps) => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const {toast} = useToast();
    const queryClient = useQueryClient();

    const formSchema = z.object({
        title: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.title")}),
        }),
        titleFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.title")}),
        }),
        university: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.university")}),
        }),
        universityFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.university")}),
        }),
        bio: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.bio")}),
        }),
        bioFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.bio")}),
        }),
        region: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
        regionFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
    }).partial();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const onUpdateSuccess = async (updatedMentor: Mentor) => {
        await queryClient.setQueryData(queryKeys.fetchMentors.all, (oldData?: Mentor[]) => {
            if (oldData) {
                return oldData.map((mentor) =>
                    mentor.id === updatedMentor.id ? updatedMentor : mentor
                );
            }
            return [updatedMentor];
        });
        toast({
            description: t("mentor.updateSuccess"),
        });
        onOpenChange(false);
    };

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc")
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: Partial<UpdateMentorMetaDto> & { id: number }) => updateMentorWithMetadata(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    useEffect(() => {
        if (mentor) {
            form.reset({
                title: mentor.title ?? "",
                titleFr: mentor.titleFr ?? "",
                university: mentor.university ?? "",
                universityFr: mentor.universityFr ?? "",
                bio: mentor.bio ?? "",
                bioFr: mentor.bioFr ?? "",
                region: mentor.region ?? "",
                regionFr: mentor.regionFr ?? "",
            });
        } else {
            form.reset();
        }
    }, [isOpen, mentor]);

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (values: z.infer<typeof formSchema>) => {
        if (!mentor) return;

        const dirtyFields = form.formState.dirtyFields as Partial<Record<keyof z.infer<typeof formSchema>, boolean>>;

        const resolvePair = async (
            enKey: "title" | "university" | "bio",
            frKey: "titleFr" | "universityFr" | "bioFr"
        ): Promise<{ en: string | undefined; fr: string | undefined }> => {
            const enVal = values[enKey]?.trim();
            const frVal = values[frKey]?.trim();
            const enDirty = Boolean(dirtyFields[enKey]);
            const frDirty = Boolean(dirtyFields[frKey]);

            if (enDirty && !frDirty && enVal) {
                const fr = await translateText(enVal, "en", "fr");
                return { en: enVal, fr };
            }
            if (!enDirty && frDirty && frVal) {
                const en = await translateText(frVal, "fr", "en");
                return { en, fr: frVal };
            }
            if (enDirty && frDirty) {
                return { en: enVal ?? undefined, fr: frVal ?? undefined };
            }
            return {
                en: mentor[enKey] ?? undefined,
                fr: mentor[frKey] ?? undefined,
            };
        };

        try {
            const [titlePair, universityPair, bioPair] = await Promise.all([
                resolvePair("title", "titleFr"),
                resolvePair("university", "universityFr"),
                resolvePair("bio", "bioFr"),
            ]);

            const updateDto: Partial<UpdateMentorMetaDto> = {
                title: titlePair.en,
                titleFr: titlePair.fr,
                university: universityPair.en,
                universityFr: universityPair.fr,
                bio: bioPair.en,
                bioFr: bioPair.fr,
            };

            if (activeLocale() === "fr") {
                updateDto["regionFr"] = values.regionFr;
                updateDto["region"] = regionOptions.find(region => region.fr === values.regionFr)?.en;
            } else {
                updateDto["region"] = values.region;
                updateDto["regionFr"] = regionOptions.find(region => region.en === values.region)?.fr;
            }

            updateMutation.mutate({ ...updateDto, id: mentor.id });
        } catch (err) {
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: err instanceof Error ? err.message : t("common.errorDesc"),
            });
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
            <SheetContent className="overflow-y-scroll max-h-screen">
                <SheetHeader>
                    <SheetTitle>{t("mentor.edit")}</SheetTitle>
                    <SheetDescription>
                        {t("mentor.editDesc")}
                    </SheetDescription>
                </SheetHeader>
                <Form {...form}>
                    <form className="space-y-4">
                        <FormField
                            name="title"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.title")}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="titleFr"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.title")} (Fr)</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="university"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.university")}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="universityFr"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.university")} (Fr)</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name={activeLocale() === "fr" ? "regionFr" : "region"}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.region")}</FormLabel>
                                    <Select onValueChange={field.onChange}
                                            value={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("mentor.selectRegion")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className="max-w-[360px] overflow-auto">
                                            {regionOptions.map((region, idx) => (
                                                <SelectItem key={idx}
                                                            value={region[activeLocale() as keyof LocalizedField]}>
                                                    {region[activeLocale() as keyof LocalizedField]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>

                                    </FormDescription>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="bio"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.bio")}</FormLabel>
                                    <FormControl>
                                        <Textarea className="resize-none"{...field}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="bioFr"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("mentor.bio")} (Fr)</FormLabel>
                                    <FormControl>
                                        <Textarea className="resize-none"{...field}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>

                <SheetFooter className="mt-3">
                    <Button
                        type="button"
                        disabled={!form.formState.isDirty || updateMutation.isPending}
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                        {t("common.save")}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>

    );
};

export default MentorForm;
